import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import LogInModal from 'flarum/forum/components/LogInModal';
import TextEditor from 'flarum/common/components/TextEditor';

const rememberedTagIcons = Object.create(null);
const associationUrl = 'https://www.revivre-france.org/l-association.html';
const adhesionUrl = 'https://www.revivre-france.org/adhesion.html';
const contactUrl = 'https://www.revivre-france.org/contact.html';
const themePreviewLimit = 20;
const textColorPresets = [
  ['Rouge', '#b42318'],
  ['Orange', '#c2410c'],
  ['Ambre', '#a15c00'],
  ['Vert', '#087443'],
  ['Bleu', '#1764aa'],
  ['Violet', '#6941c6'],
  ['Rose', '#ae4787'],
  ['Gris', '#475467'],
];
let allThemesLoad = null;
let showAllThemes = false;
let customTextColor = '#1764aa';

// --- helpers -------------------------------------------------------------

function setting(key, fallback) {
  const v = app.forum.attribute(key);
  return v === undefined || v === null || v === '' ? fallback : v;
}

function applyTextColor(editor, color) {
  const [start, end] = editor.getSelectionRange();
  const openingTag = `[color=${color}]`;
  const closingTag = '[/color]';

  // Insert around the selection instead of replacing it, which works
  // with both Flarum's textarea and the optional rich-text editor.
  editor.insertAt(start, openingTag, true);
  editor.insertAt(end + openingTag.length, closingTag, true);
  editor.moveCursorTo(start + openingTag.length);
}

function allTags() {
  try {
    return app.store.all('tags') || [];
  } catch (e) {
    return [];
  }
}

function tagIcon(tag, fallback) {
  const id = tag.id();
  const icon = tag.icon();

  if (icon) rememberedTagIcons[id] = icon;

  return icon || rememberedTagIcons[id] || fallback;
}

// Rubrique réservée au staff : ni invités ni membres ne peuvent la voir
function isStaffOnly(tag) {
  return !!tag.attribute('rfStaffOnly');
}

// Toutes les rubriques (tags primaires) visibles par l'utilisateur courant
function allPrimaryTags() {
  return allTags()
    .filter((t) => t && typeof t.position === 'function' && t.position() !== null && !t.parent())
    .sort((a, b) => a.position() - b.position());
}

// Rubriques publiques -> grille + légende
function primaryTags() {
  return allPrimaryTags().filter((t) => !isStaffOnly(t));
}

// Rubriques réservées au staff -> encart dédié dans la colonne
function staffTags() {
  return allPrimaryTags().filter(isStaffOnly);
}

// Thèmes transverses = tags secondaires
function secondaryTags() {
  return allTags()
    .filter((t) => t && typeof t.position === 'function' && t.position() === null && !t.parent())
    .sort((a, b) => a.name().localeCompare(b.name(), 'fr'));
}

function loadAllThemes() {
  if (allThemesLoad) return;

  // Flarum ne précharge que trois étiquettes secondaires sur l'accueil.
  allThemesLoad = app.store.find('tags')
    .then(() => m.redraw())
    .catch(() => { allThemesLoad = null; });
}

function setVar(el, name, value) {
  if (el && value) el.style.setProperty(name, value);
}

// #RRGGBB -> rgba(r,g,b,alpha) pour les fonds pastel de la maquette
function softColor(hex, alpha) {
  if (!hex || hex.charAt(0) !== '#' || hex.length < 7) return null;
  const r = parseInt(hex.substr(1, 2), 16);
  const g = parseInt(hex.substr(3, 2), 16);
  const b = parseInt(hex.substr(5, 2), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
  return 'rgba(' + r + ',' + g + ',' + b + ',' + alpha + ')';
}

function tagCardStyle(tag) {
  const color = tag.color();
  const safeColor = /^#[0-9a-f]{6}$/i.test(color || '') ? color : '#1F4E63';

  return `--tag-bg:${safeColor};--tag-soft:${softColor(safeColor, 0.16)};`;
}

function tagHref(tag) {
  try {
    return app.route.tag(tag);
  } catch (e) {
    return '#';
  }
}

// "Libellé | /url" par ligne -> [{label, url}]
function parseReperes() {
  const raw = setting('revivreReperes', '');
  if (!raw) return [];

  return String(raw)
    .split('\n')
    .map((line) => {
      const parts = line.split('|');
      const label = (parts[0] || '').trim();
      const url = (parts[1] || '').trim();
      return label && url ? { label, url } : null;
    })
    .filter(Boolean);
}

function isTagsPage() {
  return m.route.get().split('?')[0] === app.route('tags');
}

function themeIcon(name, className) {
  const shapes = {
    lock: [
      m('rect', { x: 5, y: 10, width: 14, height: 11, rx: 2 }),
      m('path', { d: 'M8 10V7a4 4 0 0 1 8 0v3' }),
    ],
    message: [
      m('path', { d: 'M5 4h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-6 3v-5a2 2 0 0 1-1-2V6a2 2 0 0 1 3-2Z' }),
      m('path', { d: 'M7 9h10M7 13h7' }),
    ],
    compass: [
      m('circle', { cx: 12, cy: 12, r: 9 }),
      m('path', { d: 'm15.5 8.5-2.2 4.8-4.8 2.2 2.2-4.8 4.8-2.2Z' }),
    ],
    heart: m('path', { d: 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21.2l8.8-8.8a5.5 5.5 0 0 0 0-7.8Z' }),
    external: [
      m('path', { d: 'M14 4h6v6M20 4l-9 9' }),
      m('path', { d: 'M20 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5' }),
    ],
  };

  return m('svg', {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': 1.8,
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round',
    'aria-hidden': 'true',
    focusable: 'false',
  }, shapes[name]);
}

function guestHome() {
  return m('.IndexPage.RfGuestPage',
    m('main.RfGuestMain.container', [
      m('section.RfGuestHero', { 'aria-labelledby': 'rf-guest-title' }, [
        m('.RfGuestHero-content', [
          m('span.RfGuestEyebrow', [themeIcon('lock'), 'Espace réservé aux adhérents']),
          m('h1#rf-guest-title.RfGuestTitle', 'Un espace pour échanger, à votre rythme.'),
          m('p.RfGuestIntro', 'Le forum de Revivre-France permet aux adhérents de partager leur vécu, de poser leurs questions et de se soutenir dans un cadre bienveillant.'),
          m('.RfGuestActions', [
            m('button.Button.Button--primary.RfGuestLogin', { type: 'button', onclick: () => app.modal.show(LogInModal) }, 'Se connecter'),
            m('a.RfGuestLink', { href: associationUrl, target: '_blank', rel: 'noopener noreferrer' }, 'Découvrir l’association'),
          ]),
        ]),
        m('.RfGuestHero-note', { 'aria-hidden': 'true' }, [
          themeIcon('heart', 'RfGuestHero-icon'),
          m('span', 'Bienveillance'),
          m('span', 'Respect'),
          m('span', 'Entraide'),
        ]),
      ]),

      m('section.RfGuestSection', { 'aria-labelledby': 'rf-guest-benefits' }, [
        m('h2#rf-guest-benefits.RfGuestSection-title', 'Ce qui vous attend après connexion'),
        m('.RfGuestCards', [
          m('article.RfGuestCard', [
            m('span.RfGuestCard-icon', themeIcon('message')),
            m('h3', 'Échanger entre adhérents'),
            m('p', 'Posez une question ou rejoignez une discussion quand vous en avez envie.'),
          ]),
          m('article.RfGuestCard', [
            m('span.RfGuestCard-icon', themeIcon('compass')),
            m('h3', 'Trouver des repères'),
            m('p', 'Parcourez les rubriques et retrouvez les informations partagées par les membres.'),
          ]),
          m('article.RfGuestCard', [
            m('span.RfGuestCard-icon', themeIcon('heart')),
            m('h3', 'Partager avec respect'),
            m('p', 'Un lieu fondé sur l’écoute mutuelle et l’entraide, sans jugement.'),
          ]),
        ]),
      ]),

      m('section.RfGuestHelp', { 'aria-labelledby': 'rf-guest-help' }, [
        m('.RfGuestHelp-copy', [
          m('h2#rf-guest-help', 'Déjà adhérent, sans accès au forum ?'),
          m('p', 'Contactez l’association pour obtenir de l’aide avec votre compte.'),
          m('a.RfGuestHelp-link', { href: contactUrl, target: '_blank', rel: 'noopener noreferrer' }, 'Contacter l’association'),
        ]),
        m('.RfGuestHelp-membership', [
          m('span', 'Vous découvrez Revivre-France ?'),
          m('a', { href: adhesionUrl, target: '_blank', rel: 'noopener noreferrer' }, 'Découvrir l’adhésion'),
        ]),
      ]),
    ])
  );
}

// --- extension -----------------------------------------------------------

app.initializers.add('revivre/theme', () => {
  // BBCode already renders [color=...]; these controls make it easy to apply it.
  extend(TextEditor.prototype, 'controlItems', function (items) {
    items.add(
      'rf-text-color',
      m('.RfTextColorControl', [
        m('select.FormControl.RfTextColorSelect', {
          title: 'Mettre le texte sélectionné en couleur',
          'aria-label': 'Couleur du texte',
          value: '',
          onchange: (event) => {
            const color = event.currentTarget.value;
            if (color) applyTextColor(this.attrs.composer.editor, color);
            event.currentTarget.value = '';
          },
        }, [
          m('option', { value: '' }, 'Couleur…'),
          textColorPresets.map(([label, color]) => m('option', { value: color }, label)),
        ]),
        m('input.FormControl.RfTextColorCustom', {
          type: 'color',
          value: customTextColor,
          title: 'Choisir une autre couleur',
          'aria-label': 'Choisir une autre couleur',
          onchange: (event) => {
            customTextColor = event.currentTarget.value;
            applyTextColor(this.attrs.composer.editor, customTextColor);
          },
        }),
      ]),
      -5
    );
  });

  // Reprendre la discussion demandée après connexion depuis la page d'accès.
  extend(IndexPage.prototype, 'oncreate', function () {
    // IndexPage sert aussi les routes /t/... : charger les thèmes sur chacune.
    if (app.session.user) loadAllThemes();

    const destination = new URLSearchParams(window.location.search).get('return');
    if (!destination || !/^\/d\/[0-9]+(?:-[^/?#]+)?$/.test(destination)) return;

    window.setTimeout(() => {
      if (app.session.user) {
        window.location.replace(destination);
      } else {
        app.modal.show(LogInModal);
      }
    }, 0);
  });

  // Une page publique utile, sans liste vide ni commandes réservées aux membres.
  override(IndexPage.prototype, 'view', function (original) {
    return app.session.user ? original() : guestHome();
  });

  // 1) Liseré gauche des discussions = couleur du tag primaire
  const paintItem = function (_retval, vnode) {
    const discussion = this.attrs && this.attrs.discussion;
    if (!discussion || typeof discussion.tags !== 'function') return;

    const tags = discussion.tags() || [];
    const primary = tags.find((t) => t && typeof t.position === 'function' && t.position() !== null) || tags[0];
    if (!primary || typeof primary.color !== 'function') return;

    setVar(vnode.dom, '--rf-tag', primary.color());
  };

  extend(DiscussionListItem.prototype, 'oncreate', paintItem);
  extend(DiscussionListItem.prototype, 'onupdate', paintItem);

  // 2) Accueil : hero + grille de rubriques + chips de thèmes
  override(IndexPage.prototype, 'hero', function () {
    // La page /tags réutilise ce hero, puis affiche sa propre liste de rubriques.
    // Garder notre grille uniquement sur l'accueil évite le doublon.
    if (isTagsPage()) {
      return m('.RfTagsIntro.container', [
        m('h1.RfTagsIntro-title', 'Étiquettes'),
        m('p.RfTagsIntro-sub', 'Choisissez une rubrique pour parcourir les discussions.'),
      ]);
    }

    const cats = app.session.user ? primaryTags() : [];
    const themes = app.session.user ? secondaryTags() : [];
    const visibleThemes = showAllThemes ? themes : themes.slice(0, themePreviewLimit);
    const currentPath = m.route.get().split('?')[0];

    return m('.RfHome', [
      m('.RfHero', [
        m('h1.RfHero-title', setting('welcomeTitle', app.forum.attribute('title'))),
        m('p.RfHero-sub', setting('welcomeMessage', app.forum.attribute('description'))),
      ]),

      cats.length
        ? m('.RfBlock', [
            m('p.RfBlock-title', 'Explorer par rubrique'),
            m(
              '.RfCats',
              cats.map((tag) =>
                m(
                  'a.RfCat',
                  {
                    href: tagHref(tag),
                    style: tagCardStyle(tag),
                    oncreate: m.route.link,
                  },
                  [
                    m('.RfCat-icon', m('i', { className: tagIcon(tag, 'fas fa-comments') })),
                    m('.RfCat-body', [
                      m('h3.RfCat-name', tag.name()),
                      tag.description() ? m('p.RfCat-desc', tag.description()) : null,
                    ]),
                    m('span.RfCat-count', tag.discussionCount()),
                  ]
                )
              )
            ),
          ])
        : null,

      themes.length
        ? m('.RfBlock', [
            m('p.RfBlock-title', 'Filtrer par thème'),
            m(
              '.RfThemes',
              [
                visibleThemes.map((tag) => {
                  const href = tagHref(tag);
                  const active = href === currentPath;
                  return m('a.RfChip', {
                    href,
                    className: active ? 'RfChip--active' : '',
                    'aria-current': active ? 'page' : undefined,
                    oncreate: m.route.link,
                  }, tag.name());
                }),
                themes.length > themePreviewLimit
                  ? m('button.RfChip.RfChip--more', {
                      type: 'button',
                      'aria-expanded': showAllThemes,
                      onclick: () => { showAllThemes = !showAllThemes; },
                    }, showAllThemes ? 'Voir moins' : '+ Voir tous les thèmes')
                  : null,
              ]
            ),
          ])
        : null,
    ]);
  });

  // 3) Header : bouton "Retour au site"
  extend(HeaderSecondary.prototype, 'items', function (items) {
    const url = setting('revivreSiteUrl', '');
    if (!url) return;

    items.add(
      'rf-site',
      m('a.RfSiteLink', { href: url, target: '_blank', rel: 'noopener' }, [
        themeIcon('external'),
        m('span', 'Retour au site'),
      ]),
      100
    );
  });

  // 4) Colonne latérale : on retire la liste des étiquettes
  //    (les rubriques sont déjà affichées en grille en haut de page)
  extend(IndexPage.prototype, 'navItems', function (items) {
    const keys = items && items.items ? Object.keys(items.items) : [];

    keys.forEach((key) => {
      if (/^tag\d+$/.test(key) || key === 'separator' || key === 'tags' || key === 'moreTags') items.remove(key);
    });
  });

  // 5) Colonne latérale : cartes "Repères" et "Code couleur"
  extend(IndexPage.prototype, 'sidebarItems', function (items) {
    if (!app.session.user) return;
    // /tags affiche déjà toutes les rubriques : ces cartes étirent sa navigation horizontale.
    if (isTagsPage()) return;

    const reperes = parseReperes();

    if (reperes.length) {
      items.add(
        'rf-reperes',
        m('.RfSideCard', [
          m('h5.RfSideCard-title', setting('revivreReperesTitle', 'Repères')),
          reperes.map((r) => m('a.RfSideCard-link', { href: r.url }, r.label)),
        ]),
        -10
      );
    }

    const cats = primaryTags();

    if (setting('revivreShowLegend', true) && cats.length) {
      items.add(
        'rf-legend',
        m('.RfSideCard', [
          m('h5.RfSideCard-title', 'Code couleur des rubriques'),
          cats.map((tag) =>
            m('a.RfLegendRow', { href: tagHref(tag), oncreate: m.route.link }, [
              m('span.RfLegendRow-dot', { style: { backgroundColor: tag.color() || '#B5B5B5' } }),
              m('span.RfLegendRow-name', tag.name()),
            ])
          ),
        ]),
        -20
      );
    }

    // Encart des rubriques réservées au staff (Bénévoles, Archives...)
    const staff = staffTags();

    if (staff.length) {
      items.add(
        'rf-restricted',
        m('.RfSideCard.RfSideCard--restricted', [
          m('h5.RfSideCard-title', [
            m('i.fas.fa-lock'),
            m('span', setting('revivreRestrictedTitle', 'Espace réservé — accès restreint')),
          ]),
          staff.map((tag) => {
            const children = typeof tag.children === 'function' ? tag.children() || [] : [];

            return m('.RfStaffBlock', [
              m('a.RfStaffBlock-head', { href: tagHref(tag), oncreate: m.route.link }, [
                m(
                  'span.RfStaffBlock-icon',
                  {
                    style: {
                      backgroundColor: softColor(tag.color(), 0.18) || '#f0f0f0',
                      color: tag.color() || '#8a8a8a',
                    },
                  },
                  m('i', { className: tagIcon(tag, 'fas fa-folder') })
                ),
                m('b.RfStaffBlock-name', tag.name()),
              ]),
              children.length
                ? m(
                    'ul.RfStaffBlock-list',
                    children.map((child) =>
                      m('li', m('a', { href: tagHref(child), oncreate: m.route.link }, child.name()))
                    )
                  )
                : tag.description()
                ? m('ul.RfStaffBlock-list', m('li', tag.description()))
                : null,
            ]);
          }),
        ]),
        -30
      );
    }
  });
});
